import React, { useState } from 'react';

import ProcessHistory from './ProcessHistory';
import ProcessPanel, { ProcessActions } from './ProcessPanel';
import ProcessPath from './ProcessPath';
import useStoredDraft from './useStoredDraft';

import styles from './processFlow.module.scss';

/**
 * Guided view of a process: stage path + current-stage panel + audit trail.
 * The single import consumers use — `import { ProcessFlow } from '@ui'`.
 *
 * Entirely definition-driven and domain-agnostic: hand it a `definition` and a
 * `value` and it renders the workflow, whatever that workflow is. Maintenance,
 * driver hiring and anything added later all use this same component; the only
 * difference is the document describing them.
 *
 * The caller owns persistence and the transition rules (see
 * `features/fleet/processes/logic/processesLogic`): this component collects the
 * stage's field values and calls `onAdvance`. It does not decide *whether* a
 * transition is legal.
 *
 * @param {object} props.definition       process_type: { stages, fields }
 * @param {object} props.value            the process instance
 * @param {(stageKey: string|null, data: object) => (boolean|void|Promise<boolean|void>)} props.onAdvance
 *   resolve `false` (or throw) when the save failed: the draft is then kept
 * @param {string} [props.draftKey]       persist the draft in localStorage under this key
 * @param {(stageKey: string) => void} [props.onClose] close early on a terminal stage
 * @param {string[]} [props.missing]      required keys left empty (from validation)
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.loading]
 * @param {object} props.labels           all user-facing strings, resolved by the caller
 * @param {object} props.format           { when(entry), duration(entry), stage(key) }
 *
 * Layout: the blocks are flat sections divided by a hairline (`DESIGN.md`,
 * "niente card annidate") — the caller wraps the whole thing in one `.list-card`.
 * The action bar is the last block and sticks to the bottom of the viewport,
 * because a survey form is long enough that "Avanza" would otherwise be a scroll
 * away from the field being filled.
 */
export default function ProcessFlow({
  definition,
  value,
  onAdvance,
  onClose,
  missing = [],
  readOnly = false,
  loading = false,
  labels = {},
  format = {},
  onUploadPhoto,
  draftKey,
}) {
  // Draft edits live here until the stage is advanced, so a half-filled panel
  // never writes to the record. With a `draftKey` they are also kept in
  // localStorage, so a failed save or a reload does not lose them.
  const [draft, setDraft] = useStoredDraft(draftKey);
  const [advancing, setAdvancing] = useState(false);

  if (!definition || !value) return null;

  const stages = definition.stages || [];
  const currentStage = stages.find((s) => s.key === value.stage) || null;
  // Due elenchi, due griglie: i campi comuni identificano la rilevazione e
  // restano visibili in ogni passaggio, quelli dello stage sono il lavoro di
  // adesso. Tenerli in un'unica griglia li faceva anche collidere — le
  // coordinate dichiarate sono relative al proprio elenco, non alla pagina.
  const commonFields = definition.fields || [];
  const stageFields = currentStage?.fields || [];
  const values = { ...(value.data || {}), ...draft };

  const isClosed = value.status && value.status !== 'open';
  const canAdvance = !readOnly && !isClosed && !currentStage?.is_terminal;

  const resolveStage = (key) => {
    const stage = stages.find((s) => s.key === key);
    return stage
      ? { label: stage.label, icon: stage.icon, variant: stage.variant }
      : { label: format.stage?.(key) || key };
  };

  const panelProps = {
    values,
    missing,
    readOnly: readOnly || isClosed,
    onChange: (key, v) => setDraft((d) => ({ ...d, [key]: v })),
    onUploadPhoto,
  };

  return (
    <div className={styles.flow}>
      <div className={styles.pathBlock}>
        <ProcessPath
          stages={stages}
          currentKey={value.stage}
          ariaLabel={labels.pathAria}
          // Jumping straight to a stage is deliberately not offered: transitions
          // go through the panel so required fields are collected on the way.
        />
      </div>

      <form className={styles.form} onSubmit={(e) => e.preventDefault()} noValidate>
        {commonFields.length > 0 && (
          <ProcessPanel title={labels.common} eyebrow fields={commonFields} {...panelProps} />
        )}

        <ProcessPanel
          title={currentStage?.label}
          description={isClosed ? labels.closed : labels.stageHint}
          fields={stageFields}
          {...panelProps}
        />

        {canAdvance && (
          <div className={styles.actionsBar}>
            <ProcessActions
              onAdvance={async () => {
                if (advancing) return;
                setAdvancing(true);
                try {
                  // The draft is dropped only once the caller confirms the
                  // save: `false` or a throw means it failed, so keep it.
                  const result = await onAdvance?.(null, draft);
                  if (result !== false) setDraft({});
                } catch (err) {
                  console.error('Error advancing process:', err);
                } finally {
                  setAdvancing(false);
                }
              }}
              onClose={onClose ? () => onClose(null) : undefined}
              advanceLabel={labels.advance}
              closeLabel={labels.close}
              loading={loading || advancing}
            />
          </div>
        )}
      </form>

      {(value.history || []).length > 0 && (
        <section className={styles.historySection}>
          <h4 className={styles.historyTitle}>{labels.history}</h4>
          <ProcessHistory
            entries={value.history}
            resolveStage={resolveStage}
            formatWhen={format.when}
            formatDuration={format.duration}
            emptyLabel={labels.noHistory}
          />
        </section>
      )}
    </div>
  );
}
